import { listFonts } from "../services/helpers";
import API_MEDIA from '../services/api/media'
import FontFaceObserver from "fontfaceobserver";
import similarity from "../services/helpers/similarity-text";

const FONT_NAME_MATCHING_COEFFICIENT = 0.85;
function normalizeFontKey(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/["']/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[-_]+/g, ' ')
    .replace(/[^a-z0-9 ]/g, '')
    .trim();
}
export default class FontFamilyManager {

  _allFontsData = []
  fonts = []
  fontFamily = []
  fontList = {}
  _themeFonts = []
  static #instance = null;

  constructor() {
    this._indexMap = new Map();
    this._nameMap = new Map();
    this._aliasMap = new Map();
    this._similarCache = new Map();
    this._fontsData = null;
  }

  // Call once after fonts are loaded
  buildFontLookupCache() {
    const fonts = this.getAllFontsData();

    this._indexMap.clear();
    this._nameMap.clear();
    this._aliasMap.clear();
    this._similarCache.clear();

    for (const f of fonts) {
      let fontInfo = f?.info || {};
      if (typeof fontInfo === 'string') {
        try {
          fontInfo = JSON.parse(fontInfo);
        } catch (e) {
          fontInfo = {};
        }
      }

      if (f?.index) this._indexMap.set(String(f.index), f);
      if (f?.name) this._nameMap.set(normalizeFontKey(f.name), f);

      [
        f?.system_name,
        f?.postscript_name,
        f?.imagick_name,
        fontInfo?.fullName,
        fontInfo?.postscriptName,
        fontInfo?.name,
      ].forEach(alias => {
        const key = normalizeFontKey(alias);
        if (key) this._aliasMap.set(key, f);
      });
    }
  }

  static getInstance() {
    if (!FontFamilyManager.#instance) {
      FontFamilyManager.#instance = new FontFamilyManager();
    }
    return FontFamilyManager.#instance;
  }

  _initFontFamily(fonts = []) {
    this.fontFamily = fonts.reduce((acc, font) => {
      if (!font.font_family) return acc

      const fontFamily = acc.find(fontFamily => fontFamily.id === font.family_id);

      if (!fontFamily) {
        font.font_family.fonts = [{...font, font_family: null}]
        acc.push(font.font_family)
      } else {
        fontFamily.fonts.push({...font, font_family: null})
      }

      return acc
    }, [])
  }

  _initFont(fonts = []) {
    this.fonts = fonts.filter(font => !font.font_family)
  }

  _initFontList() {
    this.fontList = {};

    this.fontFamily.forEach(fontFamily => {
      fontFamily.fonts.forEach(font => {
        if (!this.fontList[fontFamily.title]) {
          this.fontList[fontFamily.title] = [];
        }

        this.fontList[fontFamily.title].push(font.index);

        // Add each font individually so it appears in the selector
        this.fontList[font.index] = font.name;
      })
    })

    this.fonts.forEach(font => {
      this.fontList[font.index] = font.name
    })
  }

  _initLoadFontFace(fonts = this._allFontsData) {
    const promises = []
    const fontList = listFonts()
    
    fonts.forEach((font) => {
      if (!fontList.includes(font.index)) return
      const fontObserver = new FontFaceObserver(font.index)
      promises.push(fontObserver.load(null, 30000).catch((e) => {
        console.log('loading font-family: ' + font.index, 'error: ' + e)
      }))
    })

    return Promise.all(promises);
  }

  findSimilarFontByIndex(fontName, { allowSimilarity = true } = {}) {
    if (!fontName) return null;

    const exact = this._indexMap.get(String(fontName));
    if (exact) return exact;

    const key = normalizeFontKey(fontName);
    if (!key) return null;

    const byName = this._nameMap.get(key);
    if (byName) return byName;

    const byAlias = this._aliasMap.get(key);
    if (byAlias) return byAlias;

    if (this._similarCache.has(key)) {
      return this._similarCache.get(key);
    }

    if (!allowSimilarity) {
      this._similarCache.set(key, null);
      return null;
    }

    const fonts = this.getAllFontsData();
    let best = null;
    let bestScore = FONT_NAME_MATCHING_COEFFICIENT;

    const keyLen = key.length;
    const keyFirst = key[0];

    for (const font of fonts) {
      const idx = font?.index ? String(font.index) : '';
      if (idx && normalizeFontKey(idx) === key) {
        best = font;
        bestScore = 1;
        break;
      }

      const name = font?.name ? String(font.name) : '';
      if (!name) continue;

      const nKey = normalizeFontKey(name);
      if (!nKey) continue;

      if (nKey[0] !== keyFirst) continue;
      if (Math.abs(nKey.length - keyLen) > 12) continue; // tune

      const score = similarity(fontName, name);
      if (score > bestScore) {
        bestScore = score;
        best = font;

        if (bestScore >= 0.93) break;
      }
    }

    this._similarCache.set(key, best);
    return best;
  }

  findSimilarFontByIndexes(fontNames) {
    let info = null;

    for (const fontName of fontNames) {
      info = this.findSimilarFontByIndex(fontName);

      if (info) {
        return info;
      }
    }

    return info;
  }

  getAllFontsData() {
    return this._themeFonts.length ? this._themeFonts : this._allFontsData;
  }

  getFontById(fontId) {
    return this.getAllFontsData().find(font => font.id === fontId)
  }

  getFontByIndex(index) {
    return this.getAllFontsData().find(font => font.index === index)
  }

  getFirstFont() {
    return this.getAllFontsData()?.[0];
  }

  getFontsFamily(index) {
    return this.fontFamily.find(
      fontFamily => fontFamily.fonts.find(font => font.index === index)
    );
  }

  getFontList() {
    return this.fontList
  }

  getAllFontList() {
    return this._allFontsData.reduce((fontList, font) => {
      if (font.font_family) {
        const familyName = font.font_family.title
        if (!fontList[familyName]) {
          fontList[familyName] = []
        }
        fontList[familyName].push(font.index)
      }

      fontList[font.index] = font.name
      return fontList
    }, {})
  }

  getAllFonts() {
    return this.getAllFontsData().reduce((acc, font) => {
      acc[font.index] = font.name
      return acc
    }, {})
  }

  getFontName(index) {
    return this.fontList[index] || ''
  }

  setThemeFonts(fonts = []) {
    this._themeFonts = fonts.map(fontIndex => {
      return this.getAllFontsData().find(font => font.index === fontIndex)
    }).filter(Boolean)

    const fontsArray = this._themeFonts.length ? this._themeFonts : this._allFontsData

    this._initFontFamily(fontsArray)
    this._initFont(fontsArray)
    this._initFontList()
  }

  async loadData() {
    if (this._allFontsData.length) return

    if (!this.promiseLoadData) {
      this.promiseLoadData = API_MEDIA.getGenerateFonts()
    }

    const { data } = await this.promiseLoadData
    this._allFontsData = data
    this._initFontFamily(this._allFontsData)
    this._initFont(this._allFontsData)
    this._initFontList()
    this.buildFontLookupCache();
  }

  load(fonts) {
    return this._initLoadFontFace(fonts)
  }
}
